import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink, History, Pencil, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, Spinner } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, asList, enc, errMsg, openApiPath } from "@/lib/api";

const SUGGEST = ["PM – Fintech", "General", "Leadership", "Technical", "Startup"];

const LabelEditor = ({ v, onSaved, onCancel }) => {
  const [val, setVal] = useState(v.label || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const label = val.trim().slice(0, 60);
    setBusy(true);
    try { await api.patch(`/profile/resume/versions/${enc(v.version_id)}`, { label }); toast.success(label ? `Named "${label}"` : "Label removed"); onSaved(label); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={`version-label-editor-${v.version_id}`}>
      <div className="flex gap-1.5">
        <Input autoFocus value={val} maxLength={60} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onCancel(); }}
          placeholder="e.g. PM – Fintech" className="h-8 bg-[#18181B] text-sm" data-testid={`version-label-input-${v.version_id}`} />
        <Button size="icon" className="h-8 w-8 shrink-0" onClick={save} disabled={busy} data-testid={`version-label-save-${v.version_id}`}>{busy ? <Spinner /> : <Check className="h-4 w-4" />}</Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={onCancel} data-testid={`version-label-cancel-${v.version_id}`}><X className="h-4 w-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-1">
        {SUGGEST.map((s) => <button key={s} type="button" onClick={() => setVal(s)} data-testid={`version-label-suggest-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-full border border-[#3F3F46] px-2 py-0.5 text-[10px] text-zinc-400 hover:border-[#D4AF37]/60 hover:text-[#F3C649]">{s}</button>)}
      </div>
    </div>
  );
};

export const ResumeVersions = ({ onRestored }) => {
  const { refreshProfile } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/profile/resume/versions")
    .then((r) => setState({ versions: asList(r.data, "versions") }))
    .catch((e) => setState({ unavailable: [404, 405].includes(e.response?.status), versions: [] }));
  useEffect(() => {
    load();
    window.addEventListener("rpopro:resume-replaced", load);
    return () => window.removeEventListener("rpopro:resume-replaced", load);
  }, []);

  const restore = async (v) => {
    setBusy(v.version_id);
    try {
      await api.post(`/profile/resume/versions/${enc(v.version_id)}/restore`);
      const p = await refreshProfile();
      toast.success(`Switched to "${v.label || v.name}"`);
      onRestored?.(p);
      window.dispatchEvent(new CustomEvent("rpopro:resume-replaced", { detail: {} }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const setLabel = (id, label) => setState((s) => ({ ...s, versions: s.versions.map((x) => (x.version_id === id ? { ...x, label } : x)) }));

  return (
    <Card data-testid="resume-versions-card">
      <div className="mb-1 flex items-center gap-2"><History className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">Resume versions</h3></div>
      <p className="mb-4 text-xs text-zinc-500">Every time you replace your main resume, the old one is kept here. Name them so switching is effortless.</p>
      {!state ? <Spinner className="text-[#D4AF37]" /> : state.unavailable ? (
        <p className="rounded-lg border border-[#27272A] bg-[#09090B] p-3 text-xs text-zinc-400" data-testid="versions-unavailable">Version history turns on as soon as the RPOPRO backend update goes live.</p>
      ) : state.versions.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="versions-empty">No previous versions yet.</p>
      ) : (
        <div className="space-y-2" data-testid="versions-list">
          {state.versions.map((v) => (
            <div key={v.version_id} className={`rounded-lg border bg-[#09090B] p-3 ${v.is_current ? "border-[#D4AF37]/40" : "border-[#27272A]"}`} data-testid={`version-${v.version_id}`}>
              {editing === v.version_id ? (
                <LabelEditor v={v} onCancel={() => setEditing(null)} onSaved={(l) => { setLabel(v.version_id, l); setEditing(null); }} />
              ) : (
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className={`truncate text-sm ${v.label ? "font-medium text-[#F3C649]" : "text-zinc-200"}`} data-testid={`version-label-${v.version_id}`}>{v.label || v.name}</span>
                      <button onClick={() => setEditing(v.version_id)} title="Rename" className="shrink-0 text-zinc-600 hover:text-[#D4AF37]" data-testid={`version-rename-${v.version_id}`}><Pencil className="h-3 w-3" /></button>
                    </div>
                    <div className="truncate font-mono text-[10px] uppercase text-zinc-600">{v.label && `${v.name} · `}{v.uploaded_at && new Date(v.uploaded_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}{v.is_current && " · current"}</div>
                  </div>
                  <button onClick={() => openApiPath(() => api.post(`/profile/resume/versions/${enc(v.version_id)}/link`)).catch((e) => toast.error(errMsg(e)))} title="View" className="text-zinc-500 hover:text-zinc-200" data-testid={`version-view-${v.version_id}`}><ExternalLink className="h-4 w-4" /></button>
                  {!v.is_current && (
                    <Button size="sm" variant="outline" onClick={() => restore(v)} disabled={!!busy} data-testid={`version-restore-${v.version_id}`}>
                      {busy === v.version_id ? <Spinner /> : <RotateCcw className="h-3.5 w-3.5" />}Use this
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
